import { Injectable, inject, signal } from '@angular/core';
import { ToastService } from '@core/services/toast.service';
import { UserApiService } from './user.service';
import {
  PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
  PAGE_REQUEST_DEFAULT_PAGE_SIZE,
  PageRequest,
  normalizePageRequest,
} from '@core/models/common.model';
import { CreateUserRequest, UpdateUserRequest, UserResponse, UserTransactionHistoryResponse } from './user.model';

@Injectable({ providedIn: 'root' })
export class UserStore {
  private readonly userApiService = inject(UserApiService);
  private readonly toastService = inject(ToastService);

  readonly users = signal<UserResponse[]>([]);
  readonly usersSearch = signal('');
  readonly usersTotalCount = signal(0);
  readonly usersPageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly usersPageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly usersTotalPages = signal(0);
  readonly selectedUser = signal<UserResponse | null>(null);
  readonly history = signal<UserTransactionHistoryResponse[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly deactivating = signal(false);
  readonly activating = signal(false);

  loadUsers(pageRequest?: PageRequest, search?: string | null): void {
    const normalizedPageRequest = normalizePageRequest({
      pageNumber: pageRequest?.pageNumber ?? this.usersPageNumber(),
      pageSize: pageRequest?.pageSize ?? this.usersPageSize(),
    });

    if (search !== undefined) {
      this.usersSearch.set(search?.trim() ?? '');
    }

    this.loading.set(true);
    this.userApiService.getUsers(normalizedPageRequest, this.usersSearch()).subscribe({
      next: (users) => {
        this.users.set(users.items);
        this.usersTotalCount.set(users.totalCount);
        this.usersPageNumber.set(users.pageNumber);
        this.usersPageSize.set(users.pageSize);
        this.usersTotalPages.set(users.totalPages);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  loadUser(userId: string): void {
    this.loading.set(true);
    this.userApiService.getUser(userId).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  loadUserHistory(userId: string): void {
    this.userApiService.getUserHistory(userId).subscribe({
      next: (history) => this.history.set(history),
      error: () => undefined,
    });
  }

  // linkRequired runs instead of the generic failure toast when the identity provider already has a login for
  // the email that Partner Hub does not know about; the caller decides whether to resubmit with the link flag.
  createUser(request: CreateUserRequest, onDone?: (user: UserResponse) => void, linkRequired?: () => void): void {
    this.saving.set(true);
    this.userApiService.createUser(request).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.users.set([...this.users(), user]);
        this.toastService.success('User created successfully');
        this.saving.set(false);
        onDone?.(user);
      },
      error: (error: unknown) => {
        this.saving.set(false);

        if (linkRequired && isLoginExistsError(error)) {
          linkRequired();
          return;
        }

        this.toastService.error('Failed to create user');
      },
    });
  }

  updateUser(userId: string, request: UpdateUserRequest, onDone?: (user: UserResponse) => void): void {
    this.saving.set(true);
    this.userApiService.updateUser(userId, request).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.upsertUser(user);
        this.loadUserHistory(userId);
        this.toastService.success('User updated successfully');
        this.saving.set(false);
        onDone?.(user);
      },
      error: () => {
        this.toastService.error('Failed to update user');
        this.saving.set(false);
      },
    });
  }

  deactivateUser(userId: string, onDone?: (user: UserResponse) => void): void {
    this.deactivating.set(true);
    this.userApiService.deactivateUser(userId).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.upsertUser(user);
        this.loadUserHistory(userId);
        this.toastService.success('User deactivated successfully');
        this.deactivating.set(false);
        onDone?.(user);
      },
      error: () => {
        this.toastService.error('Failed to deactivate user');
        this.deactivating.set(false);
      },
    });
  }

  activateUser(userId: string, onDone?: (user: UserResponse) => void): void {
    this.activating.set(true);
    this.userApiService.activateUser(userId).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.upsertUser(user);
        this.loadUserHistory(userId);
        this.toastService.success('User activated successfully');
        this.activating.set(false);
        onDone?.(user);
      },
      error: () => {
        this.toastService.error('Failed to activate user');
        this.activating.set(false);
      },
    });
  }

  private upsertUser(user: UserResponse): void {
    this.users.set(this.users().map((item) => (item.id === user.id ? user : item)));
  }
}

// The API puts the first error's code in the problem details' errorCode extension.
function isLoginExistsError(error: unknown): boolean {
  const body = (error as { error?: { errorCode?: string } } | null)?.error;
  return body?.errorCode === 'Identity.UserExistsInIdentityProvider';
}
